import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  CheckCircle, XCircle, Clock, FileText, Users,
  ChevronDown, ChevronUp, Eye, Loader2, AlertTriangle,
  ClipboardList, Mic
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ApprovalQueuePage() {
  const { token } = useAuth();
  const [sessionNotes, setSessionNotes] = useState([]);
  const [peerDocs, setPeerDocs] = useState([]);
  const [groupNotes, setGroupNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [processingIds, setProcessingIds] = useState(new Set());

  const headers = { 'Content-Type': 'application/json' };

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const [snRes, pdRes, gnRes] = await Promise.all([
        fetch(`${API}/api/session-notes/pending-approval`, { headers, credentials: 'include' }),
        fetch(`${API}/api/peer-docs/pending-approval`, { headers, credentials: 'include' }),
        fetch(`${API}/api/group-notes`, { headers, credentials: 'include' }),
      ]);
      const [sn, pd, gn] = await Promise.all([snRes.json(), pdRes.json(), gnRes.json()]);
      setSessionNotes(Array.isArray(sn) ? sn : []);
      setPeerDocs(Array.isArray(pd) ? pd : []);
      setGroupNotes(Array.isArray(gn) ? gn.filter(n => n.approval_status === 'pending') : []);
    } catch {
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const [participantNames, setParticipantNames] = useState({});
  useEffect(() => {
    const ids = new Set();
    sessionNotes.forEach(n => ids.add(n.client_id));
    peerDocs.forEach(d => ids.add(d.client_id));
    const missing = [...ids].filter(id => id && !participantNames[id]);
    if (missing.length === 0) return;

    fetch(`${API}/api/users`, { headers, credentials: 'include' }).then(r => r.json()).then(users => {
      const map = { ...participantNames };
      (Array.isArray(users) ? users : []).forEach(u => { map[u.id] = u.full_name; });
      setParticipantNames(map);
    }).catch(() => {});
  }, [sessionNotes, peerDocs]);

  const handleAction = async (type, id, action) => {
    setProcessingIds(prev => new Set(prev).add(id));
    try {
      const endpoint = type === 'session_note'
        ? `${API}/api/session-notes/${id}/approve?action=${action}`
        : type === 'peer_doc'
        ? `${API}/api/peer-docs/${id}/approve?action=${action}`
        : `${API}/api/group-notes/${id}/approve?action=${action}`;
      const res = await fetch(endpoint, { method: 'PUT', headers, credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      toast.success(`${action === 'approve' ? 'Approved' : 'Rejected'} successfully`);
      fetchPending();
    } catch {
      toast.error(`Failed to ${action}`);
    } finally {
      setProcessingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const totalPending = sessionNotes.length + peerDocs.length + groupNotes.length;

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="approval-queue-loading">
      <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="approval-queue-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="approval-queue-title">Approval Queue</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Review and approve pending notes, summaries, and documentation before they become visible to referral sources.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {totalPending > 0 && (
            <Badge variant="destructive" className="text-sm px-3 py-1" data-testid="pending-count-badge">
              {totalPending} Pending
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={fetchPending} data-testid="refresh-queue-btn">
            Refresh
          </Button>
        </div>
      </div>

      {totalPending === 0 ? (
        <Card data-testid="no-pending-items">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mb-3" />
            <h3 className="font-semibold text-lg">All Caught Up</h3>
            <p className="text-sm text-muted-foreground mt-1">No pending items require your approval.</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="session-notes" className="w-full">
          <TabsList className="bg-muted/50 border" data-testid="approval-tabs">
            <TabsTrigger value="session-notes" className="gap-1.5" data-testid="tab-session-notes">
              <FileText size={14} />
              Session Notes
              {sessionNotes.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">{sessionNotes.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="peer-docs" className="gap-1.5" data-testid="tab-peer-docs">
              <Mic size={14} />
              Peer Docs
              {peerDocs.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">{peerDocs.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="group-notes" className="gap-1.5" data-testid="tab-group-notes">
              <ClipboardList size={14} />
              Group Notes
              {groupNotes.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">{groupNotes.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Session Notes Tab */}
          <TabsContent value="session-notes" className="space-y-3 mt-4" data-testid="session-notes-tab-content">
            {sessionNotes.length === 0 ? (
              <EmptyState type="session notes" />
            ) : sessionNotes.map(note => (
              <ApprovalCard
                key={note.id}
                item={note}
                type="session_note"
                expanded={expandedItems[note.id]}
                onToggle={() => toggleExpand(note.id)}
                onAction={handleAction}
                processing={processingIds.has(note.id)}
                participantName={participantNames[note.client_id]}
                renderPreview={() => (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <NoteField label="Subjective" value={note.subjective} />
                    <NoteField label="Objective" value={note.objective} />
                    <NoteField label="Assessment" value={note.assessment} />
                    <NoteField label="Plan" value={note.plan} />
                    <NoteField label="Session Type" value={note.session_type} />
                  </div>
                )}
              />
            ))}
          </TabsContent>

          {/* Peer Docs Tab */}
          <TabsContent value="peer-docs" className="space-y-3 mt-4" data-testid="peer-docs-tab-content">
            {peerDocs.length === 0 ? (
              <EmptyState type="peer documentation" />
            ) : peerDocs.map(doc => (
              <ApprovalCard
                key={doc.id}
                item={doc}
                type="peer_doc"
                expanded={expandedItems[doc.id]}
                onToggle={() => toggleExpand(doc.id)}
                onAction={handleAction}
                processing={processingIds.has(doc.id)}
                participantName={participantNames[doc.client_id]}
                renderPreview={() => (
                  <div className="space-y-4 mt-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <NoteField label="Original Text" value={doc.original_text} highlight="original" />
                      <NoteField label="AI-Cleaned Version" value={doc.reframed_text} highlight="ai" />
                    </div>
                    <NoteField label="Interaction Type" value={doc.interaction_type} />
                  </div>
                )}
              />
            ))}
          </TabsContent>

          {/* Group Notes Tab */}
          <TabsContent value="group-notes" className="space-y-3 mt-4" data-testid="group-notes-tab-content">
            {groupNotes.length === 0 ? (
              <EmptyState type="group notes" />
            ) : groupNotes.map(note => (
              <ApprovalCard
                key={note.id}
                item={note}
                type="group_note"
                expanded={expandedItems[note.id]}
                onToggle={() => toggleExpand(note.id)}
                onAction={handleAction}
                processing={processingIds.has(note.id)}
                participantName={null}
                renderPreview={() => (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <NoteField label="Group Topic" value={note.group_topic} />
                    <NoteField label="Date" value={note.date} />
                    <NoteField label="Participant Engagement" value={note.participant_engagement} />
                    <NoteField label="Group Outcomes" value={note.group_outcomes} />
                    <NoteField label="Next Plan" value={note.next_group_plan} />
                    <NoteField label="Additional Notes" value={note.additional_notes} />
                  </div>
                )}
              />
            ))}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function ApprovalCard({ item, type, expanded, onToggle, onAction, processing, participantName, renderPreview }) {
  const authorName = item.specialist_name || item.author_name || 'Unknown';
  const createdAt = item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : '';

  const typeLabels = {
    session_note: 'Session Note',
    peer_doc: 'Peer Documentation',
    group_note: 'Group Note',
  };

  return (
    <Card className="border-l-4 border-l-amber-400 hover:shadow-md transition-shadow" data-testid={`approval-card-${item.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50" data-testid="pending-badge">
                <Clock size={12} className="mr-1" /> Pending
              </Badge>
              <Badge variant="secondary" className="text-xs">{typeLabels[type]}</Badge>
              {item.session_type && <Badge variant="secondary" className="text-xs capitalize">{item.session_type}</Badge>}
              {item.interaction_type && <Badge variant="secondary" className="text-xs capitalize">{item.interaction_type}</Badge>}
            </div>
            <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1" data-testid="submitted-by">
                <Users size={13} /> Submitted by <strong className="text-foreground">{authorName}</strong>
              </span>
              {participantName && (
                <span className="flex items-center gap-1" data-testid="participant-name">
                  Participant: <strong className="text-foreground">{participantName}</strong>
                </span>
              )}
              <span className="text-xs">{createdAt}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="text-muted-foreground"
              data-testid={`preview-toggle-${item.id}`}
            >
              <Eye size={14} className="mr-1" />
              {expanded ? 'Hide' : 'Preview'}
              {expanded ? <ChevronUp size={14} className="ml-1" /> : <ChevronDown size={14} className="ml-1" />}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              disabled={processing}
              onClick={() => onAction(type, item.id, 'reject')}
              data-testid={`reject-btn-${item.id}`}
            >
              {processing ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} className="mr-1" />}
              Reject
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={processing}
              onClick={() => onAction(type, item.id, 'approve')}
              data-testid={`approve-btn-${item.id}`}
            >
              {processing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
              Approve
            </Button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-border animate-in fade-in-0 slide-in-from-top-2 duration-200" data-testid={`preview-content-${item.id}`}>
            {renderPreview()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function NoteField({ label, value, highlight }) {
  if (!value) return null;
  const bgClass = highlight === 'ai'
    ? 'bg-emerald-50/50 border-emerald-200'
    : highlight === 'original'
    ? 'bg-slate-50 border-slate-200'
    : 'bg-muted/30 border-border';

  return (
    <div className={`rounded-lg border p-3 ${bgClass}`}>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
        {highlight === 'ai' && <AlertTriangle size={11} className="text-emerald-600" />}
        {label}
      </p>
      <p className="text-sm leading-relaxed whitespace-pre-wrap">{value}</p>
    </div>
  );
}

function EmptyState({ type }) {
  return (
    <div className="text-center py-12 text-muted-foreground" data-testid={`empty-${type.replace(/\s/g, '-')}`}>
      <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
      <p className="text-sm">No pending {type} to review.</p>
    </div>
  );
}
